import { primary, secondary, text } from '@/theme/colors/palettes';
import { TPeerSupportDashboard } from '@/types/PeerSupport.types';
import { Box, HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { format, formatDate } from 'date-fns';
import { useState } from 'react';

const MAX_BAR_HEIGHT = 140;
const BAR_WIDTH = 14;
const BAR_GAP = 4;
const COLUMN_PADDING_X = 10;
const COLUMN_PADDING_Y = 6;

const PeerSupportDashboardHistoricalChart = ({
  data,
  onBarClick,
  selectedMonth,
  activeMonthTooltip,
}: {
  data: TPeerSupportDashboard['historicalData'];
  onBarClick: (month: string) => void;
  selectedMonth?: string;
  activeMonthTooltip?: string;
}) => {
  const highestValue = Math.max(
    1,
    ...data.flatMap((monthlyData) => [monthlyData.peerReported, monthlyData.memberRequested]),
  );

  const [isChartHovered, setIsChartHovered] = useState(false);
  const [hoveredBar, setHoveredBar] = useState('');

  return (
    <VStack alignItems={'flex-start'} gap={'8px'}>
      <HStack
        gap={'10px'}
        alignItems={'flex-end'}
        onMouseEnter={() => setIsChartHovered(true)}
        onMouseLeave={() => setIsChartHovered(false)}
      >
        {data.map((monthlyData) => {
          const isMonthSelected = monthlyData.month === selectedMonth;
          const monthHasData = monthlyData.peerReported > 0 || monthlyData.memberRequested > 0;
          const isNotSelectable = !monthHasData || isMonthSelected;
          const splitDate = monthlyData.month.split('-');
          const dateToDisplay = parseInt(splitDate[1]) < 10 ? `${splitDate[0]}-0${splitDate[1]}` : monthlyData.month;

          return (
            <Tooltip
              label={
                isMonthSelected && activeMonthTooltip
                  ? activeMonthTooltip
                  : `${isMonthSelected ? 'Showing' : 'Click to show'} data for ${formatDate(new Date(dateToDisplay), 'MMMM')}`
              }
              padding={'8px'}
              borderRadius={'6px'}
              color={'text.darkBlue'}
              backgroundColor={'extra.white'}
              boxShadow={'0px 2px 8px 0px #00417933'}
              placement="bottom"
              bottom={3}
              cursor={isNotSelectable ? 'default' : 'pointer'}
              key={monthlyData.month}
            >
              <VStack
                opacity={isChartHovered ? (monthlyData.month === hoveredBar ? 1 : 0.6) : 1}
                gap={'8px'}
                paddingX={`${COLUMN_PADDING_X}px`}
                paddingY={`${COLUMN_PADDING_Y}px`}
                borderRadius={'12px'}
                border={'3px solid'}
                borderColor={isMonthSelected ? primary[200] : 'transparent'}
                backgroundColor={isMonthSelected ? primary[100] : 'transparent'}
                transition={'background-color 0.15s ease, border-color 0.15s ease'}
                onMouseEnter={() => setHoveredBar(monthlyData.month)}
                cursor={isNotSelectable ? 'default' : 'pointer'}
                onClick={() => (isNotSelectable ? {} : onBarClick(monthlyData.month))}
              >
                <VStack height={`${MAX_BAR_HEIGHT}px`} justifyContent={'flex-end'} gap={0}>
                  <HStack alignItems={'flex-end'} gap={`${BAR_GAP}px`} height={`${MAX_BAR_HEIGHT}px`}>
                    <Box
                      backgroundColor={isMonthSelected ? primary[500] : primary[300]}
                      borderRadius={'6px'}
                      height={`${Math.max((MAX_BAR_HEIGHT * monthlyData.peerReported) / highestValue, monthlyData.peerReported > 0 ? 6 : 0)}px`}
                      width={`${BAR_WIDTH}px`}
                    />
                    <Box
                      backgroundColor={isMonthSelected ? secondary[500] : secondary[300]}
                      borderRadius={'6px'}
                      height={`${Math.max((MAX_BAR_HEIGHT * monthlyData.memberRequested) / highestValue, monthlyData.memberRequested > 0 ? 6 : 0)}px`}
                      width={`${BAR_WIDTH}px`}
                    />
                  </HStack>
                </VStack>

                <VStack gap={'2px'} alignItems={'center'}>
                  <Text
                    variant={'urbanistSmallBold'}
                    color={isMonthSelected ? text.darkBlue : text.mediumGray}
                    fontWeight={isMonthSelected ? 900 : 700}
                  >
                    {format(dateToDisplay, 'MMM')}
                  </Text>
                  <HStack gap={'4px'}>
                    <Text
                      variant={'urbanistSmallBold'}
                      color={isMonthSelected ? text.darkBlue : text.mediumGray}
                      fontWeight={700}
                    >
                      {monthlyData.peerReported}
                    </Text>
                    <Text
                      variant={'urbanistSmallBold'}
                      color={isMonthSelected ? text.darkBlue : text.mediumGray}
                      fontWeight={700}
                    >
                      |
                    </Text>
                    <Text
                      variant={'urbanistSmallBold'}
                      color={isMonthSelected ? text.darkBlue : text.mediumGray}
                      fontWeight={700}
                    >
                      {monthlyData.memberRequested}
                    </Text>
                  </HStack>
                </VStack>
              </VStack>
            </Tooltip>
          );
        })}
      </HStack>

      <HStack gap={'10px'} justifyContent={'center'} alignItems={'center'} width={'full'} paddingLeft={'4px'}>
        <HStack gap={'4px'} alignItems={'center'}>
          <Box width={'10px'} height={'10px'} borderRadius={'50%'} backgroundColor={primary[300]} />
          <Text variant={'urbanistSmallBold'} color={text.mediumGray} fontWeight={600}>
            Peer Reported
          </Text>
        </HStack>
        <HStack gap={'4px'} alignItems={'center'}>
          <Box width={'10px'} height={'10px'} borderRadius={'50%'} backgroundColor={secondary[300]} />
          <Text variant={'urbanistSmallBold'} color={text.mediumGray} fontWeight={600}>
            Member Requested
          </Text>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default PeerSupportDashboardHistoricalChart;
